import React, { useContext, useState } from 'react';
import Dromo from 'dromo-uploader-react';
import { useQuery } from '@apollo/client';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { AppContext } from '@nile/components';
import { AccountClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';

import { GET_CUSTOMER_IDENTITY_TOKEN } from './dromo';

const typeHookMap = {
  uploadStep: 'UPLOAD_STEP',
  reviewStep: 'REVIEW_STEP',
  reviewPostStep: 'REVIEW_STEP_POST_HOOKS'
};

const DromoUploader = ({
  fields,
  settings,
  onResults,
  rowHooks,
  columnHooks,
  stepHooks,
  onCancel,
  customUser,
  backendSync,
  activator,
  flags,
  userToken: userTokenProps
}) => {
  const [userToken, setUserToken] = useState(null);
  const appContext = useContext(AppContext);
  const { user = {} } = appContext;
  const companyName = user?.organizationContext?.name;
  const {
    userId,
    name: userName,
    email: userEmail,
    customerId: companyId
  } = user;

  const { data: token, loading: tokenLoading, error: tokenError } = useQuery(
    GET_CUSTOMER_IDENTITY_TOKEN,
    {
      fetchPolicy: 'network-only',
      client: AccountClient,
      skip: !!userTokenProps,
      onCompleted({ getCustomerIdentityToken }) {
        setUserToken(getCustomerIdentityToken);
      }
    }
  );

  if (tokenLoading) return null;

  if (!user && !customUser) return null;

  const getHostName = () =>
    envConfig.mode === 'development' ||
    envConfig.mode === 'frontend' ||
    localStorage.getItem('frontend')
      ? window.location.hostname?.replace('mylocaldev', 'dev')
      : window.location.hostname;

  return (
    <Dromo
      licenseKey={envConfig.dromoFrontendKey}
      fields={fields}
      settings={{
        ...settings,
        developmentMode: flags.dromoDevelopmentMode,
        webhookUrl:
          userToken || userTokenProps
            ? `${localStorage?.getItem(
                'apiDomain'
              )}/accounts-web/integration/dromoCallback/?token=${userTokenProps ||
                userToken}`
            : undefined,
        backendSync
      }}
      user={
        customUser || {
          id: userId,
          name: userName,
          email: userEmail,
          companyId,
          companyName
        }
      }
      rowHooks={rowHooks}
      columnHooks={columnHooks}
      stepHooks={stepHooks?.map((hook) => ({
        ...hook,
        type: typeHookMap[hook.type]
      }))}
      onResults={onResults}
      onCancel={onCancel}
    >
      {activator || 'Launch Dromo'}
    </Dromo>
  );
};

DromoUploader.propTypes = {
  fields: PropTypes.array.isRequired,
  settings: PropTypes.object.isRequired,
  user: PropTypes.object,
  rowHooks: PropTypes.array,
  columnHooks: PropTypes.array,
  stepHooks: PropTypes.array,
  onResults: PropTypes.func,
  onCancel: PropTypes.func,
  backendSync: PropTypes.bool,
  activator: PropTypes.object,
  userToken: PropTypes.string
};

export default withLDConsumer()(DromoUploader);
